import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { motion } from "framer-motion";
import {
  Mail,
  Send,
} from "lucide-react";
import SEO from "../../components/Common/SEO";
import { generateCanonicalUrl } from "../../utils/seoUtils";
import { triggerToast } from "../../utils/toast";
import { submitFeedback } from "../../services/contactApi";
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics";

const ContactUs = () => {
  const { trackEvent } = useAnalytics();
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    subject: "general",
    description: "",
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      const feedbackData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        subject: formData.subject,
        message: formData.description,
      };

      await submitFeedback(feedbackData);

      trackEvent(ANALYTICS_EVENTS.CONVERSION.CONTACT_FORM_SUBMITTED, { subject: formData.subject });

      triggerToast("Message sent! We'll get back to you within 24 hours.", "success");
      setFormData({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        subject: "general",
        description: "",
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to send message. Please try again.";
      triggerToast(errorMessage, "error");
    } finally {
      setSending(false);
    }
  };

  const themeAccent = "#ff2f6d";
  const cardBg = "#21222d";
  const pageBg = "#171821";
  const borderColor = "#353748";
  const inputBg = "#171821";
  const subTextColor = "#a1acb8";

  return (
    <React.Fragment>
      <SEO
        title="Contact Us | KeyQuest VR Analytics Platform"
        description="Have a question or need help? Reach out to the KeyQuest team. We're here to help you optimize your VR app's performance."
        canonical={generateCanonicalUrl("/contact-us")}
      />
      <div className="page-content py-8 py-md-12 d-flex align-items-center" style={{ backgroundColor: pageBg, minHeight: "80vh", fontFamily: "Rubik, sans-serif" }}>

        <Container className="px-3 px-md-4">
          <Row className="g-4 g-md-6 align-items-center">
            {/* Left Column: Context & Info */}
            <Col lg={6} className="mb-4 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-4">
                  <span className="px-3 py-1 rounded-pill mb-3 d-inline-block" style={{ backgroundColor: "rgba(255, 47, 109, 0.1)", color: themeAccent, fontSize: "12px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    Contact Us
                  </span>
                  <h1 className="text-white fw-bold mb-3" style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)", letterSpacing: "-0.02em" }}>
                    Let's Chat.
                  </h1>
                  <p className="d-none d-lg-block" style={{ color: subTextColor, fontSize: "15px", lineHeight: "1.7", marginBottom: "30px" }}>
                    Have questions about KeyQuest? We're here to help you navigate the XR landscape with data-driven insights. Reach out and we'll get back to you shortly.
                  </p>
                </div>

                <Card className="d-none d-lg-block" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: "16px" }}>
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-3 d-flex align-items-center justify-content-center shrink-0" style={{ width: "42px", height: "42px", backgroundColor: "rgba(255, 255, 255, 0.05)", border: `1px solid ${borderColor}` }}>
                        <Mail size={18} color={themeAccent} />
                      </div>
                      <div>
                        <p className="text-white fw-semibold mb-0" style={{ fontSize: "14px" }}>Email Us</p>
                        <p className="fw-medium mb-0" style={{ color: themeAccent, fontSize: "13px" }}>support@keyquest.io</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            </Col>

            {/* Right Column: Compact Form */}
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Card style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: "20px", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
                  <CardBody className="p-4 p-md-5">
                    <Form onSubmit={handleSubmit}>
                      <Row className="mb-3">
                        <Col md={6} className="mb-3 mb-md-0">
                          <FormGroup className="mb-0">
                            <Label for="firstName" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">First Name</Label>
                            <Input
                              id="firstName"
                              placeholder="Name"
                              required
                              value={formData.firstName}
                              onChange={handleInputChange}
                              style={{
                                backgroundColor: inputBg,
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                color: "white",
                                borderRadius: "8px",
                                fontSize: "14px",
                                padding: "12px"
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label for="lastName" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">Last Name</Label>
                            <Input
                              id="lastName"
                              placeholder="Surname"
                              required
                              value={formData.lastName}
                              onChange={handleInputChange}
                              style={{
                                backgroundColor: inputBg,
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                color: "white",
                                borderRadius: "8px",
                                fontSize: "14px",
                                padding: "12px"
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label for="email" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">Email Address</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="email@example.com"
                              required
                              value={formData.email}
                              onChange={handleInputChange}
                              style={{
                                backgroundColor: inputBg,
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                color: "white",
                                borderRadius: "8px",
                                fontSize: "14px",
                                padding: "12px"
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup className="mb-3">
                        <Label for="subject" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">Subject</Label>
                        <Input
                          type="select"
                          id="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          style={{
                            backgroundColor: inputBg,
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            color: "white",
                            borderRadius: "8px",
                            fontSize: "14px",
                            padding: "12px"
                          }}
                        >
                          <option value="general">General Inquiry</option>
                          <option value="support">Technical Support</option>
                          <option value="sales">Sales & Pricing</option>
                          <option value="partnership">Partnership</option>
                          <option value="feedback">Feedback</option>
                        </Input>
                      </FormGroup>

                      <FormGroup className="mb-4">
                        <Label for="description" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">Message</Label>
                        <Input
                          type="textarea"
                          id="description"
                          placeholder="How can we help?"
                          required
                          rows={4}
                          value={formData.description}
                          onChange={handleInputChange}
                          style={{
                            backgroundColor: inputBg,
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            color: "white",
                            borderRadius: "8px",
                            fontSize: "14px",
                            padding: "12px",
                            resize: "none"
                          }}
                        />
                      </FormGroup>

                      <Button
                        type="submit"
                        disabled={sending}
                        style={{
                          backgroundColor: themeAccent,
                          border: "none",
                          borderRadius: "10px",
                          padding: "12px 30px",
                          fontWeight: 600,
                          fontSize: "15px",
                          transition: "all 0.3s ease",
                        }}
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                      >
                        {sending ? "Sending..." : <><Send size={18} /> Send Message</>}
                      </Button>
                    </Form>
                  </CardBody>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ContactUs;
